import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  User,
  LogIn,
  Phone,
  Clock,
  Star,
  ChevronDown,
  ShoppingBag,
  Settings,
  LogOut,
  ShoppingCart,
  Menu,
  X,
} from "lucide-react";
import { useApp } from "../App";
import { useState } from "react";
import { formatCanadianPhoneNumber, getLogoImageUrl } from "@/services/utlits";
import apiConfig from "@/configs/apiConfig";
import { useRestaurantStatus } from "@/hooks/useRestaurantStatus";

export function Header() {
  const { user, restaurant, isLoggedIn, logout, cartItems } = useApp();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isClosed, message } = useRestaurantStatus(restaurant);

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  const logoUrl = getLogoImageUrl("pixwipizza.png");

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo and Brand */}
          <Link
            to="/"
            className="flex items-center space-x-3 sm:space-x-4"
            onClick={closeMobileMenu}
          >
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 from-thai-500 to-thai-600 rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
                <img
                  src={logoUrl}
                  alt="Pixwi Pizza And Wings logo"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-display">
                  {restaurant?.name}
                </h1>
                <div className="hidden lg:flex items-center space-x-4 text-sm text-gray-600">
                  {restaurant?.phone && (
                    <div className="flex items-center space-x-1">
                      <Phone className="w-3 h-3" />
                      <span>
                        {formatCanadianPhoneNumber(restaurant?.phone || "")}
                      </span>
                    </div>
                  )}
                  <Badge className="bg-green-100 text-green-800 text-xs font-medium">
                    <Clock className="w-3 h-3 mr-1" />
                    Open until {apiConfig.OPEN_TIMING} PM
                  </Badge>
                  {/* <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-current" />
                    <span className="font-medium">4.8</span>
                    <span className="text-gray-400">(2.1k)</span>
                  </div> */}
                </div>
              </div>
              {/* Mobile Brand Name */}
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 font-display">
                  {restaurant?.name}
                </h1>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Cart Button */}
            <Link to="/checkout">
              <Button
                variant="outline"
                className="relative transition-all duration-300 hover:scale-105"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                <span className="hidden lg:inline">Cart</span>
                {cartItemCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center p-0 animate-bounce-gentle">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Auth Section */}
            {isLoggedIn && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-300"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left hidden lg:block">
                      <div className="text-sm font-medium">{user.name}</div>
                      {user.role == "Guest" && (
                        <div className="text-xs text-gray-500">
                          {user.email}
                        </div>
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/profile">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Order History
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/profile">
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/profile">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-300"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Log In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="btn-primary">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Cart Button */}
            <Link to="/checkout" onClick={closeMobileMenu}>
              <Button variant="outline" size="sm" className="relative">
                <ShoppingCart className="w-4 h-4" />
                {cartItemCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center p-0 animate-bounce-gentle">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 py-4 animate-slide-in-down">
            <div className="space-y-3">
              {/* Restaurant Info */}
              <div className="px-2 py-3 bg-gray-50 rounded-lg">
                {restaurant?.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                    <Phone className="w-4 h-4" />
                    <span>
                      {formatCanadianPhoneNumber(restaurant?.phone || "")}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-100 text-green-800 text-xs font-medium">
                    <Clock className="w-3 h-3 mr-1" />
                    Open until {apiConfig.OPEN_TIMING} PM
                  </Badge>
                  {/* <div className="flex items-center space-x-1 text-sm">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="font-medium">4.8</span>
                    <span className="text-gray-400">(2.1k)</span>
                  </div> */}
                </div>
              </div>

              {/* Auth Section */}
              {isLoggedIn && user ? (
                <div className="space-y-2">
                  <div className="px-2 py-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Link to="/profile" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start">
                      <ShoppingBag className="w-4 h-4 mr-3" />
                      Order History
                    </Button>
                  </Link>

                  <Link to="/profile" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start">
                      <User className="w-4 h-4 mr-3" />
                      My Account
                    </Button>
                  </Link>

                  <Link to="/profile" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start">
                      <Settings className="w-4 h-4 mr-3" />
                      Settings
                    </Button>
                  </Link>

                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" onClick={closeMobileMenu}>
                    <Button variant="ghost" className="w-full justify-start">
                      <LogIn className="w-4 h-4 mr-3" />
                      Log In
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={closeMobileMenu}>
                    <Button className="w-full btn-primary">Sign Up</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isClosed && (
        <div className="sticky top-0 z-50 bg-red-600 text-white text-center px-4 py-3 text-sm sm:text-base">
          {message}
        </div>
      )}
    </header>
  );
}
